import { isUndefined } from 'lodash';
import {
  ComOrderData,
  ComOrderDataLine,
  CreateOrderResponse,
  FieldDefinition,
  JsonSchema,
  OrderedUserType,
  OtherAdditionState,
  POLines,
  PoOrderDetailsResponse,
} from './model';
import { OrderStatusEnum } from '../utils/helpers/enum';

const extractDataFromOrder = (
  data: CreateOrderResponse,
  updateResponseOrderLines?: ComOrderDataLine[]
): ComOrderData => {
  if (isUndefined(data)) return undefined;
  const {
    skucount,
    discount,
    totalWithTax,
    invoiceSummary,
    createdAt,
    lines,
    id: orderId,
    poFile,
    status,
    refOrderNumber,
    taxAmount,
    customerName,
    customerId,
    total,
    notifyToBuyer,
    createdByName,
    distributorName,
    distributorFirmName,
    fullFillmentLocationId,
    tags,
    userType,
    orderDiscount,
    shippingAddress,
  } = data || {};
  const orderlines: ComOrderDataLine[] = lines?.map((line) => {
    const {
      productVariant,
      minOrderQty,
      quantity,
      unitPrice,
      linePriceWithTax,
      id: orderLineId,
      poFileLineId,
      discountedUnitPrice,
      discountedLinePriceWithTax,
      promotionIds,
      packSize,
      promotionNames,
      uploadedProductName,
      uploadedQty,
      lineDiscount,
      lineTax,
      linePrice,
      cfaId,
    } = line || {};
    return {
      sequence: line?.sequence,
      skuCode: productVariant?.sku || '-',
      title: productVariant?.translations?.[0]?.name || '-',
      moq: minOrderQty || 1,
      quantity,
      price: Number(unitPrice),
      newPrice: Number(discountedUnitPrice),
      total: Number(linePriceWithTax),
      orderId,
      orderLineId,
      poFileLineId: poFileLineId || updateResponseOrderLines?.[0]?.poFileLineId,
      newQty: quantity,
      productVariantId: productVariant?.id,
      deleted: false,
      isEdited: false,
      discountedPrice: Number(discountedUnitPrice),
      discountedTotal: Number(discountedLinePriceWithTax),
      promotionIds: promotionIds,
      packSize,
      promotionNames,
      uploadedProductName,
      uploadedQuantity: uploadedQty,
      lineDiscount,
      lineTax,
      linePrice,
      cfaId,
      assetUrl: productVariant?.assetUrl,
    };
  });

  return {
    skuCount: skucount?.toString() || '',
    discount: Number(discount),
    totalWithTax: Number(totalWithTax),
    invoiceNumber: '-',
    invoiceValue: Number(invoiceSummary?.totalAmount),
    createdDate: createdAt,
    lines: orderlines,
    importSource: poFile?.importSource,
    poFileId: poFile?.id,
    status:
      status === OrderStatusEnum.SUBMITTED_BY_CUSTOMER ? 'Submitted' : status,
    orderNumber: refOrderNumber,
    tax: Number(taxAmount),
    customerId,
    orderedBy: createdByName || '',
    customerName: distributorFirmName || distributorName || customerName || '',
    total: Number(total),
    notifyToBuyer: Boolean(notifyToBuyer),
    cfaId: fullFillmentLocationId,
    tags: tags,
    userType,
    orderDiscount,
    shippingAddress,
  };
};

const extractDataFromPO = (data: PoOrderDetailsResponse): ComOrderData => {
  if (isUndefined(data)) return undefined;
  const {
    skuCount,
    discount,
    totalWithTax,
    invoiceSummary,
    uploadedDate,
    lines,
    importSource,
    pofileId,
    orderState,
    refOrderNumber,
    tax,
    customerId,
    orderedBy,
    total,
    notifyToBuyer,
    customerName,
    customerFirmName,
    tags,
    userType,
    orderDiscount,
    shippingAddress,
  } = data || {};
  const polines: ComOrderDataLine[] = lines?.map((line) => {
    const {
      cfaId,
      productVariant,
      MOQ,
      unitPrice,
      quantity,
      linePriceWithTax,
      orderId,
      orderLineId,
      poFileLineId,
      distributorProductName,
      uploadedQty,
      discountedUnitPrice,
      discountedLinePriceWithTax,
      promotionIds,
      packSize,
      promotionNames,
      lineDiscount,
      lineTax,
      linePrice,
    } = line || {};
    return {
      sequence: line?.sequence,
      skuCode: productVariant?.sku || '-',
      title: productVariant?.translations?.[0]?.name || '',
      moq: MOQ || productVariant?.MOQ || 1,
      price: Number(unitPrice),
      newPrice: Number(discountedUnitPrice),
      quantity,
      total: Number(linePriceWithTax),
      orderId,
      orderLineId,
      poFileLineId,
      promotionNames,
      newQty: quantity,
      productVariantId: productVariant?.productVariantId,
      deleted: false,
      isEdited: false,
      uploadedProductName: distributorProductName,
      uploadedQty,
      discountedPrice: Number(discountedUnitPrice),
      discountedTotal: Number(discountedLinePriceWithTax),
      promotionIds: promotionIds,
      packSize,
      uploadedQuantity: uploadedQty,
      lineDiscount,
      lineTax,
      linePrice,
      cfaId,
      assetUrl: productVariant?.assetUrl,
    };
  });

  return {
    skuCount: skuCount,
    discount: Number(discount),
    totalWithTax: Number(totalWithTax),
    invoiceNumber: '-',
    invoiceValue: Number(invoiceSummary?.totalAmount),
    createdDate: uploadedDate,
    lines: polines,
    importSource,
    poFileId: pofileId,
    status:
      orderState === OrderStatusEnum.SUBMITTED_BY_CUSTOMER
        ? 'Submitted'
        : orderState,
    orderNumber: refOrderNumber,
    tax: Number(tax),
    customerId,
    orderedBy: orderedBy,
    customerName: customerFirmName || customerName,
    total: Number(total),
    notifyToBuyer: Boolean(notifyToBuyer),
    cfaId: lines?.find(({ cfaId }) => !isUndefined(cfaId))?.cfaId,
    tags,
    userType: userType as OrderedUserType,
    orderDiscount: orderDiscount,
    shippingAddress,
  };
};

const extractOrderLineFromPO = (data: POLines): ComOrderDataLine => {
  const {
    productVariant,
    MOQ,
    unitPrice,
    quantity,
    linePriceWithTax,
    orderId,
    orderLineId,
    poFileLineId,
    distributorProductName,
    uploadedQty,
    discountedUnitPrice,
    discountedLinePriceWithTax,
    promotionIds,
    packSize,
    promotionNames,
    lineDiscount,
    lineTax,
    linePrice,
  } = data;

  return {
    sequence: data?.sequence || '',
    skuCode: productVariant?.sku || '-',
    title: productVariant?.translations?.[0]?.name || '',
    moq: MOQ || productVariant?.MOQ || 1,
    price: Number(unitPrice),
    newPrice: Number(discountedUnitPrice),
    quantity: Number(quantity),
    total: Number(linePriceWithTax),
    orderId,
    orderLineId,
    poFileLineId,
    promotionNames: promotionNames || [],
    newQty: Number(quantity),
    productVariantId: Number(productVariant?.productVariantId),
    deleted: false,
    isEdited: false,
    uploadedProductName: distributorProductName,
    uploadedQty,
    discountedPrice: Number(discountedUnitPrice),
    discountedTotal: Number(discountedLinePriceWithTax),
    promotionIds: promotionIds || [],
    packSize,
    uploadedQuantity: uploadedQty,
    lineDiscount,
    lineTax,
    linePrice,
  };
};

const getOrderStatus = (status: string) => {
  switch (status) {
    case 'SubmittedByCustomer':
      return 'Submitted';
    case 'Approved':
      return 'Accepted';
    case 'WaitingForCNF':
      return 'Waiting For CNF';
    case 'AddingItems':
      return 'Draft';
    case 'Mapped':
    case 'Mapping':
      return 'Draft';
    default:
      return status;
  }
};

interface NestedFieldDefinition extends FieldDefinition {
  fields?: Record<string, NestedFieldDefinition>;
}

function convertSchemaToFields(
  schema: JsonSchema
): Record<string, NestedFieldDefinition> {
  if (schema.type !== 'object' || !schema.properties) {
    return {};
  }

  const fields: Record<string, NestedFieldDefinition> = {};
  const requiredFields = new Set(schema.required || []);

  for (const [key, propSchema] of Object.entries(schema.properties)) {
    const fieldDef: NestedFieldDefinition = {
      required: requiredFields.has(key),
      type: mapJsonSchemaType(propSchema.type),
    };

    // Add constraints if they exist
    if (
      propSchema.maxLength !== undefined ||
      propSchema.minLength !== undefined
    ) {
      fieldDef.constraints = {};
      if (propSchema.maxLength !== undefined)
        fieldDef.constraints.maxLength = propSchema.maxLength;
      if (propSchema.minLength !== undefined)
        fieldDef.constraints.minLength = propSchema.minLength;
    }

    if (propSchema?.type === 'string' && propSchema?.format !== undefined) {
      fieldDef['format'] = propSchema.format;
    }

    // Handle nested objects - keep the hierarchy
    if (propSchema.type === 'object' && propSchema.properties) {
      fieldDef.type = 'object';
      fieldDef.fields = convertSchemaToFields(propSchema);
    }

    fields[key] = fieldDef;
  }

  return fields;
}

function mapJsonSchemaType(jsonType: string): string {
  switch (jsonType) {
    case 'string':
      return 'string';
    case 'number':
    case 'int':
    case 'integer':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'object':
      return 'object';
    case 'array':
      return 'array';
    case 'date-time':
      return 'date-time';
    default:
      return 'any';
  }
}

/** Maps order/cart API payment fields into `OtherAdditionState` for OrderNoteDisplay. */
export const mapOrderPaymentToOtherDetails = (
  data?: Record<string, any> | null
): Pick<
  OtherAdditionState,
  | 'paymentTerm'
  | 'advanceAmount'
  | 'advanceAmountType'
  | 'advancePaymentAmount'
  | 'includePaymentLink'
  | 'paymentEnable'
> => {
  const advancePayment = data?.advancePayment ?? data?.amount;
  const rawType = data?.advancePaymentType ?? data?.amountType;

  return {
    paymentTerm: data?.paymentTerm ?? data?.paymentTerms ?? '',
    includePaymentLink: Boolean(data?.includePaymentLink),
    paymentEnable:
      (data?.includePaymentLink as unknown) === 1 ||
      Boolean(data?.includePaymentLink),
    advanceAmount: Number(advancePayment) || 0,
    advancePaymentAmount:
      rawType === 'percentage'
        ? (Number(data?.advancePaymentAmount) || 0) / 100
        : (Number(data?.advancePayment) || 0) / 100,
    advanceAmountType:
      rawType === 'percentage' || rawType === '%'
        ? '%'
        : rawType === 'amount' || rawType === '₹'
          ? '₹'
          : '₹',
  };
};

export {
  extractDataFromOrder,
  extractDataFromPO,
  extractOrderLineFromPO,
  getOrderStatus,
  convertSchemaToFields,
};
